import { Injectable, Injector, inject } from '@angular/core';
import { NewAndCurrentPasswordsObject, TosItem } from '@bepatient/common';
import {
  CoreBackendIDs,
  CoreHttpClient,
  CoreLogin,
  CORE_BACKEND
} from '@bepatient/core';
import { MyAccountForm } from '@bepatient/my-account-store';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { documentDetails } from './my-account.models';

@Injectable()
export class MyAccountApiService {
  private http = inject(CoreHttpClient);
  private injector = inject(Injector);

  SMART = this.injector.get(CORE_BACKEND) === CoreBackendIDs.SMART;

  // Origin Response
  myAccount: any = {
    status: 200,
    payload: {
      user_id: '115',
      items: [
        {
          type: 'my-account-password',
          label: 'Change your password',
          title: 'Please enter your new password',
          field_label: 'Your password',
          button_label: 'Reset Password',
          color: '#647aec',
          password_policy: [
            { regex: '[^]{6,}', message: '6 mininum characters' },
            {
              regex: '[A-Z]+',
              message: 'Minimum 1 upper case letter (A B C D\u2026)'
            },
            {
              regex: '[a-z]+',
              message: 'Minimum 1 lower case letter (a b c d\u2026)'
            }
          ],
          href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/me/password/12'
        },
        {
          type: 'my-account-tos',
          label: 'Terms of service',
          id: '1338',
          href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/me/tos/1338'
        },
        {
          type: 'my-account-privacy-policy',
          label: 'Privacy statement'
        },
        {
          type: 'my-account-user-preference',
          label: 'User preferences',
          fields: [
            {
              field_id: 'fld_1010',
              type: 'language',
              title: 'Language',
              description:
                'Select your preferred language that you would like to use it on your app',
              options: [
                { value: 'en', name: 'English' },
                { value: 'fr', name: 'French' }
              ],
              value: 'en'
            },
            {
              field_id: 'fld_1669',
              type: 'notification_time',
              title: 'Push notifications',
              description:
                'You can select your favorite time to receive the app notification',
              value: '11'
            },
            {
              field_id: 'fld_1763',
              type: 'notification_timezone',
              title: '',
              description: 'Your timezone selection is',
              value: 'Australia/Sydney'
            }
          ],
          button_label: 'Submit',
          href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/preference'
        }
      ]
    }
  };

  myAccountNewResponse: any = {
    status: 200,
    payload: {
      user_id: '115',
      items: [
        {
          type: 'my-account-user-preference',
          label: 'User preferences',
          fields: [
            {
              field_id: 'fld_1010',
              type: 'language',
              title: 'Language',
              description:
                'Select your preferred language that you would like to use it on your app',
              options: [
                {
                  value: 'en',
                  name: 'English'
                },
                {
                  value: 'fr',
                  name: 'French'
                }
              ],
              value: 'en'
            },
            {
              field_id: 'fld_1669',
              type: 'notification_time',
              title: 'Push notifications',
              description:
                'You can select your favorite time to receive the app notification',
              value: '11'
            },
            {
              field_id: 'fld_1763',
              type: 'notification_timezone',
              title: '',
              description: 'Your timezone selection is',
              value: 'Australia/Sydney'
            }
          ],
          button_label: 'Submit',
          href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/preference'
        },
        {
          label: 'Security & Access',
          type: 'my-account-security-access',
          subMenu: [
            {
              type: 'my-account-password',
              label: 'Change your password',
              title: 'Please enter your new password',
              field_label: 'Your password',
              button_label: 'Reset Password',
              color: '#647aec',
              password_policy: [
                {
                  regex: '[^]{6,}',
                  message: '6 mininum characters'
                },
                {
                  regex: '[A-Z]+',
                  message: 'Minimum 1 upper case letter (A B C D ...)'
                },
                {
                  regex: '[a-z]+',
                  message: 'Minimum 1 lower case letter (a b c d ...)'
                }
              ],
              href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/me/password/12'
            }
          ]
        },
        {
          label: 'Terms of Use',
          type: 'my-account-terms-of-use',
          subMenu: [
            {
              type: 'my-account-tos',
              label: 'Study Information',
              id: '1338',
              href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/me/tos/1338'
            },
            {
              type: 'my-account-privacy-policy',
              label: "App's terms of use"
            }
          ]
        },
        {
          label: 'My Signed documents',
          type: 'my-account-signed-documents',
          subMenu: [
            {
              attachment_type: 'SIGNATURE',
              href: 'https://dev-smart-webservice.bepatientsolutions.com/rest/users/me/document/list?page_id=1726&attachment_type=SIGNATURE',
              id: '1726',
              label: 'Informet Consent Form',
              type: 'my-account-document'
            }
          ]
        },
        {
          label: 'Help & Support',
          type: 'my-account-help-support'
        }
      ]
    }
  };

  loadForms(): Observable<MyAccountForm[]> {
    return this.http
      .get<any>('/rest/users/me/profile', { withMobiCredentials: true })
      .pipe(map(res => this.formsParser(res)));
  }

  loadTosItems(url?: string): Observable<TosItem[]> {
    if (!this.SMART) {
      url = '/rest/users/me/tos';
    }
    return this.http
      .get<any>(url, { withMobiCredentials: true })
      .pipe(map(res => this.tosNewItemsParser(res.payload.items)));
  }

  loadTosDetails(url): Observable<any> {
    return this.http
      .get<any>(url, { withMobiCredentials: true })
      .pipe(map(res => res.payload));
  }

  setNewPassword(
    password: NewAndCurrentPasswordsObject,
    deviceType?: string,
    appVersion?: string
  ): Observable<Partial<CoreLogin>> {
    const url = this.SMART
      ? '/rest/users/me/password'
      : '/api/users/me/password';
    return this.http
      .post<any>(
        url,
        this.SMART
          ? {
              old_password: password.currentPassword,
              new_password: password.newPassword,
              confirm_password: password.newPassword,
              device_type: deviceType,
              app_version: appVersion
            }
          : {
              old_password: password.currentPassword,
              new_password: password.newPassword
            },
        { withMobiCredentials: true }
      )
      .pipe(
        map(res => ({
          iAuth: res.payload.iauth || '',
          tokenValidationCheckFrequency:
            res.payload.token_validation_check_frequency || void 0,
          tokenWarningExpirationDuration:
            res.payload.token_warning_expiration_duration || void 0
        }))
      );
  }

  setNewEmail(
    oldEmail: string,
    newEmail: string,
    newEmailConfirmation: string
  ): Observable<any> {
    return this.http.post(
      '/api/users/me/change-email',
      {
        old_email: oldEmail,
        new_email: newEmail,
        new_email_confirmation: newEmailConfirmation
      },
      { withMobiCredentials: true }
    );
  }

  loadFormHref(url: string): Observable<string> {
    return this.http
      .get<any>(url, { withMobiCredentials: true })
      .pipe(map(res => this.formHrefParser(res)));
  }

  loadDocumentHref(url: string): Observable<any> {
    // return of(this.documentParser(this.mockedDocumentDetails()));
    return this.http.get<any>(url, { withMobiCredentials: true }).pipe(
      map(res => {
        if (res && res.payload) {
          return this.documentParser(res.payload);
        }
      })
    );
  }

  setUserPreference(userPreference: any): Observable<any> {
    const url = userPreference.url;
    return this.http.post<any>(url, userPreference.payload, {
      withMobiCredentials: true
    });
  }

  loadHelpContent(): Observable<any> {
    const url: string = '/rest/users/help_page';
    return this.http
      .get<any>(url, { withMobiCredentials: true })
      .pipe(map(res => res));
  }

  private formsParser(data: any): MyAccountForm[] {
    this.SMART ? (data = data.payload.items) : (data = data);

    // data.push({
    //   type: 'my-account-document',
    //   label: 'eConsent',
    //   color: '#6B3CD9',
    //   href: 'https://dev.smart-connect.bepatientsolutions.com/edocuments?type=econsent&page_id=123'
    // });
    const getFormType = (item: any) => {
      switch (item.type) {
        case 'my-account-tos':
          return (item.icon = 'file-tray-full');

        case 'my-account-password':
          return (item.icon = 'barcode-outline');

        case 'my-account-privacy-policy':
          return (item.icon = 'information-circle');

        case 'my-account-document':
          return (item.icon = 'document-lock');

        case 'my-account-user-preference':
          return (item.icon = 'settings');

        case 'my-account-help-support':
          return (item.icon = 'help-circle');

        case 'my-account-terms-of-use':
          return (item.icon = 'document-text');

        case 'my-account-security-access':
          return (item.icon = 'lock-closed');

        case 'my-account-signed-documents':
          return (item.icon = 'checkmark-done-circle');

        default:
          return (item.icon = 'alert-circle-outline');
      }
    };

    if (this.SMART) {
      data.forEach(item => {
        getFormType(item);
        (item.subMenu || []).forEach(subItem => {
          getFormType(subItem);
        });
      });
    }

    return (data || ([] as Array<any>)).map(item => ({
      buttonLabel: item.button_label || void 0,
      color: item.color || void 0,
      fieldLabel: item.field_label || void 0,
      href:
        (item.links && item.links.self && item.links.self.href) ||
        item.href ||
        '',
      icon: (item.meta && item.meta.icon) || item.icon || '',
      label: (item.meta && item.meta.title) || item.label || '',
      passwordPolicy: item.password_policy || void 0,
      title: item.title || void 0,
      type: item.type || '',
      fields: item.fields || [],
      subMenu: (item.subMenu || []).map(subItem => ({
        buttonLabel: subItem.button_label || void 0,
        color: subItem.color || void 0,
        fieldLabel: subItem.field_label || void 0,
        href:
          (subItem.links && subItem.links.self && subItem.links.self.href) ||
          subItem.href ||
          '',
        icon: (subItem.meta && subItem.meta.icon) || subItem.icon || '',
        label: (subItem.meta && subItem.meta.title) || subItem.label || '',
        passwordPolicy: subItem.password_policy || void 0,
        title: subItem.title || void 0,
        type: subItem.type || '',
        fields: subItem.fields || []
      }))
    }));
  }

  private tosNewItemsParser(data: any): TosItem[] {
    return (data || ([] as Array<any>)).map(item => ({
      uuid: void 0,
      title: item.title || '',
      excerpt: item.abstract || '',
      content: item.button_link || '',
      required: false,
      toggleText: item.toggleText || '',
      buttonLabel: item.button_label || '',
      videoType: item.video_type || '',
      type: item.type || '',
      url: item.url || ''
    }));
  }

  private formHrefParser(data: any): string {
    return (
      (data.items &&
        data.items[0] &&
        data.items[0].links &&
        data.items[0].links.self &&
        data.items[0].links.self.href) ||
      (data.links && data.links.self && data.links.self.href) ||
      ''
    );
  }

  private documentParser(data: any): documentDetails {
    if (data && data.attachments && data.attachments.length > 0) {
      return {
        current: {
          sectionLabel: data.latest_section_label || '',
          icon: 'document-outline',
          contentType: 'application/pdf',
          fileType: 'pdf',
          attachment: {
            name: data.attachments[0].file_name || '',
            href: data.attachments[0].href || void 0
          }
        },
        previous: {
          sectionLabel: data.previous_section_label || '',
          icon: 'document-outline',
          contentType: 'application/pdf',
          fileType: 'pdf',
          attachment: data.attachments.slice(1)
        },
        title: data.title || '',
        label: data.label || '',
        buttonLabel: data.button_label || '',
        removeSectionLabel: data.remove_section_label || '',
        removeSectionDescription: data.remove_section_description || ''
      };
    }
  }

  private mockedTosItems(): any {
    return {
      status: 200,
      payload: [
        {
          abstract: '',
          content: '',
          title: 'CGU'
        },
        {
          abstract: '',
          content: '',
          title: ''
        }
      ]
    };
  }

  private mockedTosItems2(): any[] {
    return [
      {
        uuid: void 0,
        title: 'CGUs Default',
        excerpt: 'Ceci est un résumé\r\nLorem ipsum dolor sit amet',
        content: '<p>Ceci est le texte en entier</p>',
        required: false,
        toggleText: ''
      }
    ];
  }

  private mockedForms(): Observable<MyAccountForm[]> {
    return of<MyAccountForm[]>([
      {
        href: '...',
        icon: 'person',
        label: 'Mon profil'
      },
      {
        href: '...',
        icon: 'egg',
        label: 'Mes informations médicales'
      },
      {
        href: '...',
        icon: 'cog',
        label: 'Mes réglages'
      }
    ]);
  }

  private mockedDocumentDetails(): any {
    return {
      attachments: [
        {
          name: 'Kecsonsen-2022-12-22.pdf',
          href: 'https://local-smart-webservice/edocuments?attachment_id=123'
        },
        {
          name: 'Kecsonsen-2022-12-20.pdf',
          href: 'https://local-smart-webservice/edocuments?attachment_id=123'
        },
        {
          name: 'Kecsonsen-2022-12-20.pdf',
          href: 'https://local-smart-webservice/edocuments?attachment_id=123'
        },
        {
          name: 'Kecsonsen-2022-12-20.pdf',
          href: 'https://local-smart-webservice/edocuments?attachment_id=123'
        }
      ],
      button_label: 'K: Remove My Econsent',
      latest_section_label: 'K: Your Econsent Document',
      previous_section_label: 'K: Previous Econsent',
      remove_section_label: 'K: Remove My Econsent',
      remove_section_description: 'K: Please click button below to remove the '
    };
  }
}
